import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check for referral code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
    }

    // Check for token in URL (from OAuth callback)
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl) {
      localStorage.setItem('token', tokenFromUrl);
      setToken(tokenFromUrl);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Check localStorage
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        setToken(savedToken);
      } else {
        // No token at all → not authenticated, stop loading
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Fetch user info when token changes
    if (token) {
      fetchUser();
      // Auto-apply referral code if exists and not already used
      applyReferralCodeIfExists();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        trackAffiliateSignupIfExists();
      } else {
        // Token invalid
        logout();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      // Always release the loading gate AFTER the user fetch finishes.
      // Without this, protected pages (e.g. /espace-client, /admin, /ma-licence)
      // saw `loading=false` + `user=null` for a brief moment and immediately
      // redirected to "/", which felt like a broken link.
      setLoading(false);
    }
  };

  const loginWithDiscord = () => {
    window.location.href = `${API_URL}/api/auth/discord`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const trackAffiliateSignupIfExists = async () => {
    const affCode = localStorage.getItem('affiliate_code');
    const currentToken = token || localStorage.getItem('token');
    if (!affCode || !currentToken) return;
    try {
      await fetch(`${API_URL}/api/affiliates/track-signup/${affCode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
    } catch (error) {
      console.error('Error tracking affiliate signup:', error);
    }
  };

  const applyReferralCodeIfExists = async () => {
    const refCode = localStorage.getItem('referral_code');
    if (!refCode || !token) return;

    try {
      const response = await fetch(`${API_URL}/api/referral/use?referral_code=${refCode}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        console.log('Referral code applied successfully');
        localStorage.removeItem('referral_code'); // Remove after use
      }
    } catch (error) {
      console.error('Error applying referral code:', error);
    }
  };

  const createOrder = async (serviceType, affiliateCode = null) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_URL}/api/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        service_type: serviceType,
        affiliate_code: affiliateCode
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create order');
    }

    return response.json();
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    loginWithDiscord,
    logout,
    createOrder,
    token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
